import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Activity, Database, Mail, Rss, Users } from "lucide-react";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { Nav } from "../components/landing/Nav";
import { Footer } from "../components/landing/Footer";

const formatWeek = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });

const MetricsPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get("/metrics/summary")
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  const cards = data
    ? [
        { label: "Borrower Enquiries (Total)", value: data.enquiries_total, icon: Mail, testId: "metric-enquiries-total" },
        { label: "Borrower Enquiries (7d)", value: data.enquiries_7d, icon: Activity, testId: "metric-enquiries-7d" },
        { label: "Brief Subscribers (Total)", value: data.subscribers_total, icon: Users, testId: "metric-subscribers-total" },
        { label: "Brief Subscribers (7d)", value: data.subscribers_7d, icon: Activity, testId: "metric-subscribers-7d" },
        { label: "Managers Listed", value: data.managers_total, icon: Database, testId: "metric-managers-total" },
        { label: "News Items Indexed", value: data.news_items_indexed, icon: Rss, testId: "metric-news-indexed" },
      ]
    : [];

  return (
    <div className="bg-navy text-ink min-h-screen antialiased" data-testid="metrics-page">
      <Seo
        title="Site Metrics"
        description="Aggregate site activity metrics for privatecredit.com.au: enquiry, subscriber, directory and press feed counters. No personal data is published."
        path="/metrics"
        noindex
      />
      <Nav />
      <main className="max-w-6xl mx-auto px-6 py-16">
        <span className="text-xs font-mono text-cy uppercase tracking-widest">[TELEMETRY] ACTIVITY REGISTER</span>
        <h1 className="font-display text-4xl md:text-6xl text-white uppercase mt-2">
          Site <span className="text-cy">Metrics</span>
        </h1>
        <p className="text-base md:text-lg text-mut leading-relaxed mt-4 max-w-3xl">
          Aggregate counters demonstrating that the name attracts visitors, enquiries and subscribers. Figures
          are totals only — no personal data is displayed.
        </p>

        {error && (
          <div className="mt-10 p-6 rounded-xl bg-surface border border-line font-mono text-xs text-mut" data-testid="metrics-error">
            Metrics feed temporarily unavailable.
          </div>
        )}

        {!data && !error && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="metrics-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="p-6 rounded-xl bg-surface border border-line animate-pulse h-28" />
            ))}
          </div>
        )}

        {data && (
          <>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 font-mono">
              {cards.map((c) => (
                <div key={c.testId} className="p-6 rounded-xl bg-surface border border-line" data-testid={c.testId}>
                  <div className="flex items-center gap-2 text-xs text-mut uppercase">
                    <c.icon className="w-4 h-4 text-cy" /> {c.label}
                  </div>
                  <div className="text-4xl font-display text-white mt-2">{c.value}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 rounded-xl bg-surface border border-line" data-testid="metrics-weekly-chart">
              <div className="text-xs font-mono text-cy uppercase tracking-widest mb-4">
                WEEKLY ACTIVITY — LAST 8 WEEKS
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.weekly} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <XAxis
                      dataKey="week_start"
                      tickFormatter={formatWeek}
                      tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "IBM Plex Mono, monospace" }}
                      axisLine={{ stroke: "#1E2E42" }}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "IBM Plex Mono, monospace" }}
                      axisLine={{ stroke: "#1E2E42" }}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#0B131E",
                        border: "1px solid #1E2E42",
                        borderRadius: 8,
                        fontFamily: "IBM Plex Mono, monospace",
                        fontSize: 12,
                      }}
                      labelStyle={{ color: "#38BDF8" }}
                      labelFormatter={formatWeek}
                      cursor={{ fill: "rgba(56, 189, 248, 0.08)" }}
                    />
                    <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 11 }} />
                    <Bar dataKey="enquiries" name="ENQUIRIES" fill="#38BDF8" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="subscribers" name="SUBSCRIBERS" fill="#F1F5F9" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <p className="mt-6 text-[10px] font-mono text-slate-500">
              GENERATED {new Date(data.generated_at).toLocaleString("en-AU").toUpperCase()} &middot; COUNTS ARE
              AGGREGATES ONLY
            </p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default MetricsPage;
